import React, { useState } from "react";
import Utils, { sendAppEvent } from "../../../Utils";
import { EMAIL_US_AT } from "../constants";
import { StyledAIWordsPopover, Container } from "./styles";
import classnames from "classnames";
import SVG from "react-inlinesvg";
import { useLocation } from "react-router-dom";
import { formatDateWithTimezone } from "../../../utils/formatDateWithTimezone";
import { manuscriptFeedbackPaidPlanTitleAllowed } from "../../../../../src/shared/manuscriptFeedbackPlanAccess.js";
const dateFormat = require("dateformat");

/**
 * Renders one pricing column that represents a plan
 *
 * @param {Object} params
 * @param {String} params.position The position of the column. If "comparison" then it is the left-most current plan comparison (in the case that the current plan is not within the pricing version we offer), otherwise it is 0, 1, 2
 * @param {Object} plan The plan to show
 * @param {Object} currentPlan The user's current plan (if any)
 * @param {Object} subscription The user's subscription object
 * @param {Boolean} submitting Whether a request to change plan is in flight
 * @param {Boolean} legacy Whether this is a column showing a plan in legacy state
 * @param {Boolean} isRevcatSubscription Whether the subscription is a revenuecat subscription
 * @returns
 */
export default function PricingColumn({
  position, //
  plan,
  currentPlan,
  handleSelectPlan,
  subscription,
  hasFreePlan,
  isMobileView,
  churnkeyToken,
  submitting,
  legacy,
  user,
  hasFreeTrial,
  isRevcatSubscription,
  storeInfo,
  isStoreSubscriber,
  disableStoreActions,
  isMobileDevice,
}) {
  const { price, monthlyPrice, allotment, description, description2, id, interval, version } = plan;
  const [popoverShowing, setPopoverShowing] = useState(false);
  const [popoverHovering, setPopoverHovering] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const eventSignup = location.pathname === "/event/get-in/";

  const isCurrentPlan = currentPlan?.id === id;
  const isNextPlan = subscription?.nextPlan()?.id === id;

  const isV4Transition = isNextPlan && currentPlan?.version[0] !== "4" && plan?.version[0] === "4";

  const cents = monthlyPrice % 100;
  const dollars = (monthlyPrice - cents) / 100;
  const isPopular = plan.popular && !legacy && !isV4Transition;
  const showDoubling = !isCurrentPlan && !isV4Transition;
  let title;

  if (legacy) {
    title = "Your current plan:";
  } else if (isV4Transition) {
    title = "Your upcoming plan:";
  } else {
    title = plan.title;
  }

  let currentPrice = currentPlan ? currentPlan.price : null;
  if (hasFreePlan) currentPrice = 0;

  const storeLink = storeInfo?.manageUrl || null;
  const showStoreButton = isStoreSubscriber && storeInfo && !disableStoreActions;

  let buttonText = "Start Free Trial";
  let buttonDisabled = submitting;

  if (isStoreSubscriber && storeInfo) {
    buttonText = `Open ${storeInfo.storeName}`;
    buttonDisabled = submitting || !storeLink;
  } else if (eventSignup) {
    buttonText = "Join";
    buttonDisabled = submitting;
  } else if (currentPrice !== null) {
    buttonText = !isCurrentPlan && (plan.version[0] !== subscription.data.version[0] || currentPlan.interval !== interval || price < currentPrice) ? "Switch" : "Upgrade";
    buttonDisabled = submitting;
  } else if (submitting) {
    buttonText = "Loading...";
  } else if (subscription) {
    buttonText = "Subscribe";
  }

  const handleStoreButtonClick = () => {
    if (!storeLink) return;

    sendAppEvent("Pricing - Store Link Clicked", {
      plan_id: plan.id,
      plan_title: plan.title,
      store: storeInfo.storeName,
      store_key: subscription?.data?.subscriptionSource,
    });

    if (isMobileDevice) {
      window.location.href = storeLink;
    } else {
      window.open(storeLink, "_blank", "noopener,noreferrer");
    }
  };

  const onPrimaryClick = () => {
    if (buttonDisabled) return;

    if (isStoreSubscriber && storeInfo) {
      handleStoreButtonClick();
      return;
    }

    handleSelectPlan(subscription, plan);
  };

  const samePlanButDifferentInterval = currentPlan && currentPlan.version[0] === version[0] && currentPlan.allotment === allotment && currentPlan.interval !== interval;

  const showPopover = isMobileView ? popoverShowing : popoverHovering;

  const manualContact = isNextPlan || isV4Transition || (legacy && subscription.nextPlan()); // cases where we need to manually contact to change billing

  const cancelContactButtons = !isRevcatSubscription ? (
    <>
      {isV4Transition && (
        <button
          className={"under-buttons"}
          onClick={() => {
            handleSelectPlan(subscription, plan);
          }}
        >
          Activate Now
        </button>
      )}
      {currentPlan && !manualContact && (
        <button
          disabled={!churnkeyToken}
          className={"under-buttons"}
          onClick={() => {
            sendAppEvent("Wants to pause or cancel", {
              current_plan_id: currentPlan?.id,
              current_plan_title: currentPlan?.title,
              current_plan_price: currentPlan?.price,
              source: "pricing_column_pause_cancel_button",
            });

            let pausedOrCancelled = false;

            const attributes = {
              margin: subscription?.stats?.margin,
              months_active_subscription: subscription?.stats?.months_active_subscription,
              days_active_subscription: subscription?.stats?.days_active_subscription,
            };

            if (!window.churnkey?.init) {
              window.location.href = "/app/subscription/portal";
              return;
            }

            window.churnkey.init("show", {
              customerId: subscription?.data?.stripeCustomerId,
              authHash: churnkeyToken,
              appId: "8fpinw773",
              mode: process.env.NODE_ENV === "production" ? "live" : "test",
              provider: "stripe",
              record: true,
              customerAttributes: attributes,
              onClose: () => {
                if (!pausedOrCancelled) {
                  window.location.reload();
                }
              },
              onPause: () => {
                pausedOrCancelled = true;
              },
              onCancel: () => {
                pausedOrCancelled = true;
              },
            });
          }}
        >
          Pause or Cancel Plan
        </button>
      )}
      {manualContact && (
        <a href={`mailto:${EMAIL_US_AT}?subject=Pause or Cancel My Sudowrite plan`}>
          <button className="under-buttons">Pause or Cancel Plan</button>
        </a>
      )}
      <button
        className="under-buttons"
        onClick={(e) => {
          window.location.href = "/app/subscription/portal";
        }}
      >
        Manage Billing
      </button>

      <a href={`mailto:${EMAIL_US_AT}?subject=Question about my Sudowrite plan`}>
        <button className="under-buttons">Contact Us</button>
      </a>
    </>
  ) : null;

  const toggleAIWordsPopover = () => {
    if (isMobileView) setPopoverShowing(!popoverShowing);
  };

  const currentOrUpcomingPlanText = ({ variant }) => {
    if (!subscription) return null;

    let text;
    if (legacy) {
      text = "This is a legacy plan.";
    } else if (isNextPlan) {
      text = "Your upcoming plan";
    } else if (isCurrentPlan) {
      text = "Your current plan";
    }

    if (text) {
      return <div className={classnames("current-plan", { legacy, [variant]: variant })}>{text}</div>;
    } else {
      return null;
    }
  };

  const currentOrUpcomingPlanUnder = () => {
    if (!subscription) return null;

    if (subscription.canceled()) {
      return (
        <>
          <span className="under-column-text">Your plan will cancel on {dateFormat(subscription.nextBillingDate(), "mmmm dd, yyyy")}</span>

          {!((legacy && currentPlan.version[0] !== "4") /* allow renewals on legacy plans in 4.0, but not for other legacy plans < 4 */) && (
            <>
              <p>Changed your mind?</p>
              <button
                disabled={submitting}
                onClick={(e) => {
                  handleSelectPlan(subscription, plan);
                }}
              >
                Renew your plan
              </button>
            </>
          )}
        </>
      );
    } else if (subscription.paused) {
      return (
        <>
          <span className="under-column-text">Your plan is paused and will resume on {dateFormat(subscription.resumesAtDisplay, "mmmm dd, yyyy")}</span>

          <button
            disabled={loading}
            onClick={(e) => {
              setLoading(true);
              subscription.resume().then((result) => {
                setTimeout(() => {
                  window.location.reload();
                }, 5000);
              });
            }}
          >
            {loading ? "Resuming... please wait" : "Resume your plan"}
          </button>
        </>
      );
    } else if (isNextPlan) {
      if (isV4Transition) {
        return (
          <>
            <span className="under-column-text">
              Your plan will switch to {subscription.nextPlan().title} on <br /> {formatDateWithTimezone(subscription.nextPlanDateOrNextBillingDate())}. <br />
              <br /> This is a plan with a special bonus that is only available to early users of Sudowrite.
            </span>
            {cancelContactButtons}
          </>
        );
      } else {
        return (
          <>
            <span className="under-column-text">
              Your plan will switch to {subscription.nextPlan().title} on <br /> {formatDateWithTimezone(subscription.nextPlanDateOrNextBillingDate())}
            </span>
            {cancelContactButtons}
          </>
        );
      }
    } else if (legacy && subscription?.nextPlan()) {
      // shadow legacy with a next plan: we need to show it under or else they won't know the downgrade is happening
      return (
        <>
          <span className="under-column-text">
            Your plan will switch to {subscription.nextPlan().title} on <br /> {formatDateWithTimezone(subscription.nextPlanDateOrNextBillingDate())}
          </span>
          {cancelContactButtons}
        </>
      );
    } else if (isCurrentPlan && !subscription?.nextPlan()) {
      return (
        <>
          <span className="under-column-text">
            Your plan renews on <br /> {formatDateWithTimezone(subscription.nextBillingDate())}
          </span>
          {subscription?.data.provisioned && subscription.nextProvisionDate() && subscription?.currentPlan()?.interval === "year" && (
            <span className="under-column-text">
              Your credits refresh on <br /> {formatDateWithTimezone(subscription.nextProvisionDate())}
            </span>
          )}

          {cancelContactButtons}
        </>
      );
    }
  };

  const pauseCancelText = plan.interval === "year" ? "Cancel anytime." : "Pause or cancel anytime.";

  return (
    <Container
      position={position}
      className={classnames({
        popular: isPopular,
        [`position-${position}`]: true,
      })}
    >
      <div className="content">
        {isPopular && <h4 className="popular-banner">Most Popular</h4>}
        <div className="column-header">
          <h3>{title}</h3>
          <div className="description">
            <p>{description}</p>
            {description2 && <p className="description2">{description2}</p>}
          </div>
        </div>
        <h5>
          <div>$</div>
          {dollars} {cents > 0 && <div>.{String(cents).padStart(2, "0")}</div>}{" "}
        </h5>
        <span className="time-period">per month</span>

        <div className={classnames("allotment-section", { "double-display": plan.doubled })}>
          {!plan.hideAllotment && (
            <>
              {plan.doubled && showDoubling && (
                <div className="doubled">
                  {Utils.numberWithCommas(allotment)}
                  <div>
                    We <strong>doubled</strong> it!
                    <SVG src="/assets/pricing-arrow.svg" />
                  </div>
                </div>
              )}
              {plan.crossedOutAllotment && showDoubling && (
                <div className={classnames({ doubled: showDoubling && plan.doubled, quadrupled: showDoubling && plan.crossedOutAllotment })}>
                  {Utils.numberWithCommas(allotment)}
                  <div>
                    <strong>2X</strong> as much!
                    <SVG src="/assets/pricing-arrow.svg" />
                  </div>
                </div>
              )}

              <h6 className={classnames({ half: showDoubling && (plan.doubled || plan.crossedOutAllotment) })}>
                {showDoubling && plan.doubled && <>{Utils.numberWithCommas(allotment / 2)}</>}
                {showDoubling && plan.crossedOutAllotment && <>{Utils.numberWithCommas(plan.crossedOutAllotment)}</>}
                {((!plan.doubled && !plan.crossedOutAllotment) || !showDoubling) && <>{Utils.numberWithCommas(allotment)}</>}
              </h6>

              <div
                className="words-explainer"
                onClick={toggleAIWordsPopover}
                onMouseEnter={() => {
                  setPopoverHovering(true);
                }}
                onMouseLeave={() => {
                  setPopoverHovering(false);
                }}
              >
                {`${plan.usesCredits ? "credits" : "AI words"} per month`}
                {showPopover && (
                  <StyledAIWordsPopover
                    isMobile={isMobileView}
                    popoverHovering={popoverHovering}
                    popoverShowing={popoverShowing}
                  >
                    {isMobileView && <div onClick={toggleAIWordsPopover}>&times;</div>}
                    {plan.usesCredits && (
                      <>
                        <strong>What's a credit?</strong>
                        <br />
                        Sudowrite features use credits based on the AI model and the number of words read from your text and written for you.
                      </>
                    )}
                    {!plan.usesCredits && (
                      <>
                        <strong>AI Words</strong> is the limit on the number of words Sudowrite writes each month. There’s no limit on how many words you can write yourself.
                      </>
                    )}
                  </StyledAIWordsPopover>
                )}
              </div>
            </>
          )}
        </div>

        <div className="bottom">
          {isStoreSubscriber && storeInfo ? (
            <>
              {currentOrUpcomingPlanText({ variant: "short" })}
              <div className="button-container">
                {showStoreButton ? (
                  <>
                    <button
                      disabled={buttonDisabled}
                      onClick={onPrimaryClick}
                    >
                      {buttonText}
                    </button>
                    <span>Manage your subscription through the {storeInfo.storeName}.</span>
                  </>
                ) : (
                  <>
                    <span className="mb-10 mr-5 ml-5">Manage your subscription through the {storeInfo.storeName}.</span>
                  </>
                )}
              </div>
            </>
          ) : isRevcatSubscription ? null : !isCurrentPlan && !isNextPlan ? (
            <div className="button-container">
              <button
                onClick={() => {
                  handleSelectPlan(subscription, plan);
                }}
              >
                {buttonText}
              </button>
              {!subscription && !eventSignup ? <span>No credit card, cancel anytime.</span> : <span>{pauseCancelText}</span>}
            </div>
          ) : (
            currentOrUpcomingPlanText({ variant: "tall" })
          )}
        </div>
        {plan.title !== "Limited Free" && (
          <ul className="features">
            <li>Writing tools with story intelligence</li>
            <li>Match your writing style</li>
            <li>Maintain continuity across your series</li>
            <li>Fiction chat for brainstorming, research, and editing</li>
            <li>
              Muse &amp; Ballad
              <small>+30 industry-leading models</small>
            </li>
            {manuscriptFeedbackPaidPlanTitleAllowed(plan.title) && (
              <li>
                <strong>Developmental edits, line edits, and beta reads</strong>
              </li>
            )}
            {plan.usesCredits && plan.rollover && (
              <li>
                <strong>Unused credits roll over for 12 months</strong>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* {legacy && <div className="legacy">This plan available only to early users of Sudowrite. If you cancel or change your plan, you will no longer have access to this plan.</div>} */}

      {samePlanButDifferentInterval && <div className="intervalMessage">You are on the {interval === "year" ? "month" : "year"}ly version of this plan</div>}

      {(isCurrentPlan || isNextPlan) && (
        <div className="under">
          {hasFreePlan && !subscription?.nextPlan() ? (
            <span className="under-column-text">
              Your plan renews on <br /> {formatDateWithTimezone(subscription.nextBillingDate())}
            </span>
          ) : (
            currentOrUpcomingPlanUnder()
          )}
        </div>
      )}
    </Container>
  );
}
